/**
 * Replaces images that can't be loaded (deleted from storage, expired link) with a grey
 * "Image not available" box of the same size, so the layout holds and the gap is explained.
 *
 * The preview iframe runs no scripts, so images are checked here, before the HTML is shown.
 */

const MISSING_IMAGE_CLASS = 'vh-missing-image';
const PROBE_TIMEOUT_MS = 10_000;

const MISSING_IMAGE_STYLES = `
  .${MISSING_IMAGE_CLASS} { width: 100%; height: 100%; min-height: 120px; box-sizing: border-box; background: #eeeeee;
    border: 2px dashed #bdbdbd; color: #757575; display: flex; align-items: center; justify-content: center;
    font-size: 16px; text-align: center; }
  .photo .${MISSING_IMAGE_CLASS} { width: 450px; height: 322px; }
`;

// Signed URLs differ per request, so remember results by file, for the life of the page.
const results = new Map<string, Promise<boolean>>();

const fileOf = (src: string) => src.split('?')[0];

const canLoad = (src: string): Promise<boolean> => {
  const key = fileOf(src);
  const known = results.get(key);
  if (known) return known;

  const probe = new Promise<boolean>(resolve => {
    const img = new Image();
    const timer = window.setTimeout(() => resolve(false), PROBE_TIMEOUT_MS);
    img.onload = () => {
      window.clearTimeout(timer);
      resolve(img.naturalWidth > 0);
    };
    img.onerror = () => {
      window.clearTimeout(timer);
      resolve(false);
    };
    img.src = src;
  });
  results.set(key, probe);
  return probe;
};

export const replaceUnavailableImages = async (html: string): Promise<string> => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Embedded images (logo, signature) are always available.
  const images = Array.from(doc.querySelectorAll('img')).filter(img => {
    const src = img.getAttribute('src') || '';
    return src && !src.startsWith('data:');
  });
  if (images.length === 0) return html;

  const available = await Promise.all(images.map(img => canLoad(img.getAttribute('src') as string)));
  let replaced = 0;
  images.forEach((img, i) => {
    if (available[i]) return;
    const box = doc.createElement('div');
    box.className = `${MISSING_IMAGE_CLASS} ${img.className}`.trim();
    box.textContent = 'Image not available';
    img.replaceWith(box);
    replaced++;
  });
  if (replaced === 0) return html;

  const style = doc.createElement('style');
  style.textContent = MISSING_IMAGE_STYLES;
  doc.head.appendChild(style);
  return `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
};
